import * as React from 'react';
import {v4 as uuid} from 'uuid';
import styled, {css} from 'styled-components';
import {transparentize} from 'polished';
import SlideDown from 'react-slidedown';
import {ContentType} from 'api/lesson/types';
import LessonBuilderContext from 'components/LessonBuilder/LessonBuilderContext';
import {transition} from 'helpers/css';
import outline from 'helpers/outline';
import {IconRoundPlus} from 'icons';
import {CardProps} from '..';

export interface SeparatorProps {
  index: CardProps['index'];
}

export interface ControlObject {
  type: ContentType;
  label: string;
}

const Container = styled.div`
  padding: 10px 0;
`;

const Header = styled.button`
  width: 100%;
  column-gap: 10px;
  grid-template-columns: max-content max-content 1fr;
  align-items: center;
  grid-auto-flow: column;
  display: grid;

  padding: 0;
  user-select: none;
  ${transition()};

  text-align: left;
  color: ${({theme}) => `
    ${transparentize(.7, theme.variables.color)};
  `};

  &:hover,
  &:focus-visible {
    color: ${({theme}) => `
      ${transparentize(.5, theme.variables.color)};
    `};
  }

  &:active {
    color: ${({theme}) => `
      ${transparentize(.3, theme.variables.color)};
    `};
  }

  &:focus-visible {
    color: ${({theme}) => theme.colors.primary};
  }

  &:after {
    content: "";

    width: 100%;
    margin-top: 4px;
    border-bottom: 1px dashed ${({theme}) => `
      ${transparentize(.85, theme.variables.color)};
    `};
  }
`;

const IconContainer = styled.div<{
  isToggle?: boolean;
}>`
  display: grid;
  ${transition()};
  color: ${({theme}) => `
    ${transparentize(.85, theme.colors.white)};
  `};
  
  ${({isToggle}) => isToggle && css`
    transform: rotate(45deg);
  `};
`;

const Controls = styled.div`
  margin-top: 10px;
  column-gap: 6px;
  align-items: center;
  grid-auto-flow: column;
  display: inline-grid;
`;

const Control = styled.button`
  padding: 6px 10px;
  box-sizing: border-box;
  border-radius: 6px;

  font-size: 12px;
  line-height: 16px;

  ${transition()};
  user-select: none;

  background: ${({theme}) => `
    ${transparentize(.9, theme.colors.white)};
  `};

  &:hover {
    background: ${({theme}) => `
      ${transparentize(.8, theme.colors.white)};
    `};
  }

  &:active {
    background: ${({theme}) => `
      ${transparentize(.7, theme.colors.white)};
    `};
  }

  &:focus-visible {
    ${outline(4, 1)};
  }
`;

/**
 * This component renders separate with the controls for adding cards.
 * @param {SeparatorProps} props
 * @return {React.ReactElement<SeparatorProps>}
 * @constructor
 */
export default function Separator(
    props: SeparatorProps,
): React.ReactElement<SeparatorProps> {
  const {lesson, setLesson} = React.useContext(LessonBuilderContext);
  const content = lesson?.content || [];

  const [
    isToggle,
    setToggle,
  ] = React.useState<boolean>(false);

  const controls: ControlObject[] = [
    {
      type: ContentType.Text,
      label: 'Текст',
    },
    {
      type: ContentType.Image,
      label: 'Изображение',
    },
    {
      type: ContentType.Gallery,
      label: 'Галерея',
    },
    {
      type: ContentType.Video,
      label: 'Видео',
    },
    {
      type: ContentType.Document,
      label: 'Документ',
    },
  ];

  /**
   * Render header.
   * @return {JSX.Element}
   */
  function renderHeader(): JSX.Element {
    /**
     * This click event handler.
     */
    function handleClick(): void {
      setToggle(!isToggle);
    }

    return (
      <Header onClick={handleClick}>
        <IconContainer isToggle={isToggle}>
          <IconRoundPlus />
        </IconContainer>
        Добавить блок
      </Header>
    );
  }

  /**
   * Render controls
   * @return {JSX.Element}
   */
  function renderControls(): JSX.Element {
    /**
     * Render the control.
     * @param {ControlObject} control
     * @param {number} index
     * @return {JSX.Element}
     */
    function renderControl(
        control: ControlObject,
        index: number,
    ): JSX.Element {
      /**
       * This click event handler.
       */
      function handleClick(): void {
        content.splice(props.index, 0, {
          id: uuid(),
          contentType: control.type,
          value: undefined,
        });

        (lesson && setLesson) && (
          setLesson({
            ...lesson,
            content,
          })
        );
      }

      return (
        <Control key={index} onClick={handleClick}>
          {control.label}
        </Control>
      );
    }

    return (
      <SlideDown closed={!isToggle}>
        <Controls>
          {controls.map((control, index) => (
            renderControl(control, index)
          ))}
        </Controls>
      </SlideDown>
    );
  }

  return (
    <Container>
      {renderHeader()}
      {renderControls()}
    </Container>
  );
}
